import React, { useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Button, notification, Skeleton } from 'antd'
import { Flex, Header, Icon } from 'govwell-ui'
import { AicaEmailDigestFrequency } from 'types/graphql'

import { useSendTestAicaDigestMutation } from 'src/fetch/aicaEmailDigestSubscriptions'
import {
  useActiveStaffMembershipsQuery,
  useUpdateUserAicaEmailPreferenceMutation,
} from 'src/fetch/userOrganizationMemberships'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMyOrg, useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey } from 'src/utils/queryClient'

import SendTestDigestModal from './SendTestDigestModal'
import StaffDigestTable from './StaffDigestTable'

const EmailInsightsTab = () => {
  const queryClient = useQueryClient()
  const organizationId = useMyOrgId()
  const org = useMyOrg()

  const isLegacyTrial = org?.aiResidentAssistantUsageTier === 'AicaLegacyTrial'

  const { data: staffMembers, isLoading: isLoadingStaff } = useActiveStaffMembershipsQuery()
  const { mutateAsync: sendTestDigest, isPending: isSendingTest } = useSendTestAicaDigestMutation()
  const { mutateAsync: updateAicaEmailPreference, isPending: isUpdatingPreference } =
    useUpdateUserAicaEmailPreferenceMutation()

  const sendTestModalState = useDisclosure()

  const invalidateStaffMemberships = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ActiveStaffMemberships] })
  }, [queryClient])

  const handleToggleDigestOptOut = useCallback(
    async (userId: number, shouldOptOut: boolean) => {
      if (!organizationId) {
        return
      }
      try {
        await updateAicaEmailPreference({
          input: { organizationId, userId, emailType: 'Digest', shouldOptOut },
        })
        invalidateStaffMemberships()
      } catch {
        notification.error({ message: 'Failed to update digest preference' })
      }
    },
    [updateAicaEmailPreference, organizationId, invalidateStaffMemberships]
  )

  const handleToggleUsageNoticeOptOut = useCallback(
    async (userId: number, shouldOptOut: boolean) => {
      if (!organizationId) {
        return
      }
      try {
        await updateAicaEmailPreference({
          input: { organizationId, userId, emailType: 'UsageNotice', shouldOptOut },
        })
        invalidateStaffMemberships()
      } catch {
        notification.error({ message: 'Failed to update usage notice preference' })
      }
    },
    [updateAicaEmailPreference, organizationId, invalidateStaffMemberships]
  )

  const handleSendTestDigest = useCallback(
    async (emailAddress: string, frequency: AicaEmailDigestFrequency) => {
      try {
        await sendTestDigest({ input: { emailAddress, frequency, organizationId } })
        sendTestModalState.close()
        notification.success({
          message: 'Test digest sent',
          description: `A test ${frequency.toLowerCase()} digest has been sent to ${emailAddress}.`,
        })
      } catch {
        notification.error({
          message: 'Failed to send test digest',
          description: 'Please check the email address and try again.',
        })
      }
    },
    [sendTestDigest, organizationId, sendTestModalState]
  )

  const stableStaffMembers = useMemo(
    () =>
      (staffMembers ?? [])
        .filter((m): m is typeof m & { user: NonNullable<(typeof m)['user']> } => m.user != null)
        .map((m) => ({
          id: m.id,
          userId: m.user.id,
          group: m.group ?? null,
          isAIStaffMember: m.isAIStaffMember ?? false,
          isOptedOutFromAicaDigest: m.isOptedOutFromAicaDigest ?? null,
          isOptedOutFromAicaUsageNotice: m.isOptedOutFromAicaUsageNotice ?? null,
          user: m.user,
        })),
    [staffMembers]
  )

  const isProcessing = isUpdatingPreference

  return (
    <>
      <Flex direction="column" gap={4} align="stretch">
        <Header
          title="Email Digest"
          subtitle="Manage which staff members receive automated Community Assistant emails."
          actions={
            <Button
              icon={<Icon.FaEnvelope />}
              onClick={sendTestModalState.open}
              disabled={isProcessing || isSendingTest}
            >
              Send test digest
            </Button>
          }
        />

        {isLoadingStaff ? (
          <Skeleton active />
        ) : (
          <StaffDigestTable
            staffMembers={stableStaffMembers}
            onToggleDigestOptOut={handleToggleDigestOptOut}
            onToggleUsageNoticeOptOut={handleToggleUsageNoticeOptOut}
            showUsageNoticeColumn={isLegacyTrial}
            isProcessing={isProcessing}
          />
        )}
      </Flex>

      {sendTestModalState.isOpen && (
        <SendTestDigestModal
          onClose={sendTestModalState.close}
          onSend={handleSendTestDigest}
          isLoading={isSendingTest}
        />
      )}
    </>
  )
}

export default React.memo(EmailInsightsTab)
